/**
 * maintenanceApi.js — API functions for the Maintenance Report page.
 *
 * fetchMaintenanceReport — runs the maintenance report query (cloud + on-premise)
 *
 * Filter options are re-exported from alertDetailsApi.js to avoid duplication.
 */

// Re-export shared filter options fetcher
import { getAuthHeaders } from '../../../../services/auth';

export { fetchFilterOptions } from '../../alert-details/api/alertDetailsApi.js';

// ---------------------------------------------------------------------------
// Internals (same base URL / header pattern)
// ---------------------------------------------------------------------------

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => getAuthHeaders();

// ---------------------------------------------------------------------------
// Fetch Maintenance Report
// ---------------------------------------------------------------------------

/**
 * Runs the maintenanceReport query with all filter fields.
 *
 * Supports both cloud and on-premise variants:
 * - Cloud: includes `is_cloud: true` in the payload
 * - On-premise: includes `campus_guid` in the payload
 *
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} [opts.campusGuid]  — campus GUID for on-premise
 * @param {boolean} opts.isCloud       — whether this campus is cloud-based
 * @param {string} opts.username
 * @param {string} opts.startDate      — ISO format (yyyy-mm-dd hh:mm:ss)
 * @param {string} opts.endDate        — ISO format
 * @param {object} opts.filters        — all report-level filter selections
 * @returns {Promise<object>} — { records, overlimit, url, ErrorCode }
 */
export async function fetchMaintenanceReport({ campusId, campusGuid, isCloud, username, startDate, endDate, filters = {} }) {
    const body = {
        rep_type: 'maintenanceReport',
        username: username || '',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        event_category_filter: '2',
        device_category_filter: filters.deviceCategories || '',
        device_filter: filters.devices || '',
        area_type_filter: filters.areaTypes || '',
        reason_filter: filters.reasons || '',
        user_filter: filters.users || '',
        event_type_filter: filters.eventTypes || '',
        resident_filter: filters.residents || '',
        resident_group_filter: filters.residentGroups || '',
        area_filter: filters.areas || '',
        area_group_filter: filters.areaGroups || '',
        user_group_filter: filters.userGroups || '',
    };

    // Cloud vs on-premise identifier
    if (isCloud) {
        body.is_cloud = true;
    } else {
        body.campus_guid = campusGuid || '';
    }

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(body),
        });

        if (resp.status === 503) return { ErrorCode: 11 };
        if (resp.status === 500) return { ErrorCode: 10 };
        if (resp.status === 403) return { ErrorCode: 12 };

        const raw = await resp.json();

        if (raw?.ErrorCode) {
            return { ErrorCode: raw.ErrorCode };
        }

        // Report endpoint wraps data in { Status, Res: {...} }
        const data = raw?.Res || raw;

        if (data?.ErrorCode) {
            return { ErrorCode: data.ErrorCode };
        }

        return {
            records: data?.records || [],
            overlimit: Boolean(data?.overlimit),
            url: data?.url || '',
        };
    } catch (err) {
        console.error('[maintenanceApi] fetchMaintenanceReport error:', err);
        return { ErrorCode: 10 };
    }
}
